import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { claimVacancy, releaseVacancy } from '@/api/queries';
import { api } from '@/lib/api';
import { safeAlert, safeHaptic } from '@/lib/telegramSafe';

interface MeAnalytics {
  vacancy_id: string;
  title: string;
  status: string;
  total_applications: number;
  scored_applications: number;
  shortlisted: number;
  avg_score: number | null;
  last_score_at: string | null;
  mine?: boolean;
  last_cold_search_at?: string | null;
  /** Філія (PeopleForce department) — the same role runs in several of them. */
  branch?: string | null;
  city?: string | null;
  /** Who took the vacancy on; `null` = вільна, її можна забрати собі. */
  recruiter_id?: string | null;
  recruiter_name?: string | null;
}

/** What the list is narrowed to. «Вільні» is the queue to pick work from;
 * «На паузі» is the parked work, kept out of the other three on purpose —
 * see :func:`countsOf`. */
type Scope = 'mine' | 'free' | 'all' | 'paused';

type Ownership = 'mine' | 'free' | 'other';

async function fetchAnalytics(): Promise<MeAnalytics[]> {
  return api.get<MeAnalytics[]>('/api/v1/me/analytics');
}

const STATUS_LABEL: Record<string, string> = {
  active: 'активна',
  paused: 'на паузі',
  draft: 'чернетка',
  closed: 'закрита',
};

/** `recruiter_id` decides first, `mine` only says whether that owner is me.
 * An older build sent `mine` for «я тут колись запускав пошук» too; a client
 * still holding such a row must not show «Відмовитись», because DELETE /claim
 * on an unclaimed vacancy is a no-op and the button would look broken. So an
 * unowned row is «Забрати собі» whatever `mine` says. */
function ownerState(row: MeAnalytics): Ownership {
  if (row.recruiter_id == null) return 'free';
  return row.mine === true ? 'mine' : 'other';
}

/** «Активна» is the only status the system acts on: the nightly cold search
 * runs on it (since 2026-08-20 a paused vacancy is skipped) and it is the
 * work a recruiter picks up. Roman, 2026-08-20: the chips read «Вільні (36) ·
 * Усі (74)» over a fleet he counts as 11 and 39 — the difference was the
 * paused half of the pool sitting silently inside every count. */
function isActive(row: MeAnalytics): boolean {
  return row.status === 'active';
}

/** «Назва · Філія» — the identity of one row. Same title, three branches is
 * the normal shape here; the city stands in when PeopleForce left the
 * department empty. */
function rowQualifier(row: MeAnalytics): string {
  return (row.branch ?? '').trim() || (row.city ?? '').trim();
}

/** What the nightly cycle will do with this vacancy tomorrow. «далі щодня» is
 * a promise, and on a paused vacancy it is now a false one. */
function coldSearchLine(row: MeAnalytics): string {
  const last = row.last_cold_search_at
    ? `Холодний пошук: ${new Date(row.last_cold_search_at).toLocaleDateString('uk-UA')}`
    : 'Холодний пошук не запускався';
  if (!isActive(row)) return `${last} · автопошук зупинено, поки вакансія на паузі`;
  return row.last_cold_search_at
    ? `${last} · далі щодня`
    : `${last} — щоденний пошук по цій вакансії не працює`;
}

/** What to say when the fleet is not empty but this view is. Without it the
 * «Вільні» chip would report «Нічого не знайдено за запитом «»». */
function emptyRowsHint(scope: Scope, needle: string): string | null {
  if (needle) return `Нічого не знайдено за запитом «${needle}».`;
  if (scope === 'mine')
    return 'Ви ще не взяли жодної вакансії. Відкрийте «Вільні» й натисніть «Забрати собі» — далі картки з кандидатами по ній приходитимуть вам.';
  if (scope === 'free') return 'Вільних вакансій немає — усі вже хтось веде.';
  if (scope === 'paused') return 'Вакансій на паузі немає.';
  return null;
}

export default function AnalyticsScreen(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['my-analytics'],
    queryFn: fetchAnalytics,
  });
  // HR Lead / admin now get the whole fleet here (dozens of rows), and this
  // list is the only route to the candidates and cold-search screens — so
  // finding one vacancy on a phone has to be possible without scrolling.
  const [query, setQuery] = useState('');
  // `null` = the recruiter has not chosen a scope yet, so the default may
  // follow the data (see `activeScope`). A tapped chip is a decision and
  // stays put even when it shows nothing — that empty «Мої» is exactly where
  // a newcomer is told to go and take a vacancy.
  const [scope, setScope] = useState<Scope | null>(null);

  // «Усі» means every vacancy someone is hiring for — paused ones are counted
  // apart, under their own chip. Before that split the three numbers silently
  // mixed the two pools and matched nothing the recruiter could count in
  // PeopleForce (Roman, 2026-08-20).
  const counts = useMemo(() => {
    const list = data ?? [];
    let mine = 0;
    let free = 0;
    let all = 0;
    let paused = 0;
    for (const row of list) {
      if (!isActive(row)) {
        paused += 1;
        continue;
      }
      all += 1;
      const state = ownerState(row);
      if (state === 'mine') mine += 1;
      else if (state === 'free') free += 1;
    }
    return { mine, free, all, paused };
  }, [data]);

  // Self-healing default: the server returns the whole shared pool, and
  // someone who has claimed nothing would otherwise stare at an empty list
  // behind a filter they never set.
  const activeScope: Scope = scope ?? (counts.mine > 0 ? 'mine' : 'all');

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let out = data ?? [];
    out = activeScope === 'paused' ? out.filter((row) => !isActive(row)) : out.filter(isActive);
    if (activeScope === 'mine' || activeScope === 'free')
      out = out.filter((row) => ownerState(row) === activeScope);
    // The branch is part of the needle: with the same title in three branches
    // typing the title alone cannot get you to one row.
    if (needle)
      out = out.filter((row) =>
        `${row.title} ${rowQualifier(row)}`.toLowerCase().includes(needle),
      );
    return out;
  }, [data, query, activeScope]);

  // Also on failure: a 409 means somebody claimed it first, and refetching is
  // what makes «не красти чужі» visible instead of just an alert.
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['my-analytics'] });
  };

  const claim = useMutation({
    mutationFn: (vacancyId: string) => claimVacancy(vacancyId),
    onSuccess: () => {
      safeHaptic('success');
      // Claiming happens from «Усі»/«Вільні». Pin that scope: otherwise the
      // row we just took makes «Мої» non-empty and the default view flips to
      // it, collapsing the list under the finger that tapped.
      setScope(activeScope);
    },
    onError: (err: Error) => {
      safeHaptic('error');
      safeAlert(`Не вдалося забрати вакансію: ${err.message}`);
    },
    onSettled: refresh,
  });

  const release = useMutation({
    mutationFn: (vacancyId: string) => releaseVacancy(vacancyId),
    onError: (err: Error) => {
      safeHaptic('error');
      safeAlert(`Не вдалося відмовитись від вакансії: ${err.message}`);
    },
    onSettled: refresh,
  });

  const busy = (vacancyId: string): boolean =>
    (claim.isPending && claim.variables === vacancyId) ||
    (release.isPending && release.variables === vacancyId);

  const hint = rows.length === 0 ? emptyRowsHint(activeScope, query.trim()) : null;

  return (
    <div className="flex flex-col gap-3 p-4 pb-24">
      <h1 className="text-lg font-semibold">Вакансії</h1>
      {isLoading && <div className="text-tg-hint">Завантаження…</div>}
      {isError && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-700">
          Помилка: {(error as Error).message}
        </div>
      )}
      {data && data.length > 0 && (
        <>
          <label htmlFor="vacancy-search" className="sr-only">
            Пошук вакансії
          </label>
          <input
            id="vacancy-search"
            name="vacancySearch"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Пошук за назвою"
            className="w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
          />
          {/* Shown even to someone who owns nothing: under self-claiming they
              are precisely the people who need to switch to «Вільні». */}
          <div className="flex gap-2 text-xs">
            <FilterChip active={activeScope === 'mine'} onClick={() => setScope('mine')}>
              Мої ({counts.mine})
            </FilterChip>
            <FilterChip active={activeScope === 'free'} onClick={() => setScope('free')}>
              Вільні ({counts.free})
            </FilterChip>
            <FilterChip active={activeScope === 'all'} onClick={() => setScope('all')}>
              Усі ({counts.all})
            </FilterChip>
            {/* Hidden when there is nothing parked — but never while it is the
                open view, or the chip would vanish from under the finger. */}
            {(counts.paused > 0 || activeScope === 'paused') && (
              <FilterChip active={activeScope === 'paused'} onClick={() => setScope('paused')}>
                На паузі ({counts.paused})
              </FilterChip>
            )}
          </div>
        </>
      )}
      {data && data.length === 0 && (
        <div className="rounded bg-tg-secondary p-3 text-sm text-tg-hint">
          Вакансій поки не видно. Тепер рекрутер сам забирає вакансію зі спільного списку — щойно
          вони приїдуть із PeopleForce, відкрийте «Вільні» й заберіть свою.
        </div>
      )}
      {data && data.length > 0 && hint && (
        <div className="rounded bg-tg-secondary p-3 text-sm text-tg-hint">{hint}</div>
      )}
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.vacancy_id}
            className="rounded border border-tg-secondary p-3 text-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium">
                {row.title}
                {rowQualifier(row) && (
                  <span className="ml-1 font-normal text-tg-hint">· {rowQualifier(row)}</span>
                )}
              </span>
              {row.status !== 'active' && (
                <span className="shrink-0 rounded-full bg-tg-secondary px-2 py-0.5 text-[10px] uppercase text-tg-hint">
                  {STATUS_LABEL[row.status] ?? row.status}
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <Stat label="Аплікації" value={row.total_applications} />
              <Stat label="Скоринг" value={row.scored_applications} />
              <Stat label="Шортлист" value={row.shortlisted} />
            </div>
            <div className="mt-2 text-xs text-tg-hint">
              Середній бал: {row.avg_score?.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '—'} · оновлено{' '}
              {row.last_score_at
                ? new Date(row.last_score_at).toLocaleString('uk-UA')
                : '—'}
            </div>
            {/* A vacancy nobody has ever cold-searched is never picked up
                by the nightly cycle, and until now it looked exactly like
                one that ran last night and found nobody. */}
            <div className="mt-1 text-xs text-tg-hint">{coldSearchLine(row)}</div>
            {/* Two explicit destinations instead of one whole-card link: the
                cold-search screen used to be reachable only by tapping the
                deep-link button on an individual Telegram card. */}
            <div className="mt-3 flex gap-2">
              <Link
                to={`/vacancy/${row.vacancy_id}/candidates?title=${encodeURIComponent(row.title)}`}
                className="flex-1 rounded border border-tg-secondary py-2 text-center text-xs text-tg-link"
              >
                Кандидати
              </Link>
              <Link
                to={`/vacancy/${row.vacancy_id}/cold-search?title=${encodeURIComponent(row.title)}`}
                className="flex-1 rounded border border-tg-secondary py-2 text-center text-xs text-tg-link"
              >
                Холодний пошук
              </Link>
            </div>
            {/* Own line rather than a third link: «Забрати собі» next to both
                destinations does not fit a 360px phone at a tappable size. */}
            <div className="mt-2 flex text-xs">
              <OwnershipControl
                row={row}
                busy={busy(row.vacancy_id)}
                onClaim={() => claim.mutate(row.vacancy_id)}
                onRelease={() => release.mutate(row.vacancy_id)}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OwnershipControl({
  row,
  busy,
  onClaim,
  onRelease,
}: {
  row: MeAnalytics;
  busy: boolean;
  onClaim: () => void;
  onRelease: () => void;
}): JSX.Element {
  const state = ownerState(row);
  if (state === 'other') {
    // Read-only: taking over a colleague's vacancy is their call or the HR
    // Lead's, never a tap here.
    return (
      <span className="rounded-full bg-tg-secondary px-3 py-1 text-tg-hint">
        Веде: {row.recruiter_name?.trim() || 'інший рекрутер'}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={state === 'mine' ? onRelease : onClaim}
      disabled={busy}
      className="rounded border border-tg-secondary px-3 py-1 text-tg-link disabled:opacity-50"
    >
      {state === 'mine' ? 'Відмовитись' : 'Забрати собі'}
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 ${
        active ? 'bg-tg-link text-white' : 'bg-tg-secondary text-tg-hint'
      }`}
    >
      {children}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded bg-tg-secondary p-2 text-center">
      <div className="text-base font-semibold">{value}</div>
      <div className="text-[10px] uppercase text-tg-hint">{label}</div>
    </div>
  );
}
